/**
 * firestoreCache — module-level TTL cache backing RelayDataProvider's
 * "stable collection" reads (relay_workspaces, relay_presence).
 *
 * track:track-backlog--cc-v2-phase-1-relaydataprovider--shared-data-layer-no-ui-changes
 * (seq #28585, dev-2 finding #28573): those collections were each read by
 * a continuous onSnapshot listener in both App.tsx and ControlCenter.tsx —
 * 4x the necessary read volume for data that only needs to be "fresh
 * enough," not real-time. This cache lets a hook do a one-shot Firestore
 * read, reuse the cached value across renders/remounts until it expires,
 * then re-read — bounding read volume to one read per TTL window shared by
 * every consumer, instead of a per-mount continuous stream.
 */

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

export function getCached<T>(key: string): T | undefined {
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return undefined;
  }
  return entry.value as T;
}

export function setCached<T>(key: string, value: T, ttlMs: number): void {
  cache.set(key, { value, expiresAt: Date.now() + ttlMs });
}

export function invalidateCached(key: string): void {
  cache.delete(key);
}

export function clearCache(): void {
  cache.clear();
}
